'use client';

// components/common/PartnerHero.tsx
// Cover banner shown ONLY on a partner's own profile page (e.g. /user-profile?username=fxdoctor),
// using their 1200x628 creative. Renders nothing for non-partner profiles.

import { useEffect, useRef } from 'react';
import { track } from '@/lib/analytics';
import { partnerByUsername } from '@/lib/ads/partners';

export default function PartnerHero({ username }: { username?: string | null }) {
  const partner = partnerByUsername(username);

  // GA4 impression: fire view_promotion once when the hero scrolls into view.
  const heroRef = useRef<HTMLAnchorElement>(null);
  const viewedRef = useRef(false);
  useEffect(() => {
    const el = heroRef.current;
    if (!partner?.heroSrc || !el || viewedRef.current) return;
    const io = new IntersectionObserver(
      (entries) => {
        if (viewedRef.current || !entries.some((e) => e.isIntersecting)) return;
        viewedRef.current = true;
        io.disconnect();
        try {
          track('view_promotion', {
            promotion_id: partner.id,
            promotion_name: partner.name,
            creative_slot: 'profile_hero',
          });
        } catch {
          /* best-effort */
        }
      },
      { threshold: 0.5 },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [partner]);

  if (!partner?.heroSrc) return null;

  const handleClick = () => {
    try {
      track('select_promotion', {
        promotion_id: partner.id,
        promotion_name: partner.name,
        creative_slot: 'profile_hero',
      });
    } catch {
      /* best-effort */
    }
  };

  let href = partner.href;
  try {
    const u = new URL(partner.href);
    u.searchParams.set('utm_source', 'showmetrade');
    u.searchParams.set('utm_medium', 'profile_hero');
    u.searchParams.set('utm_campaign', partner.id);
    href = u.toString();
  } catch {
    /* keep raw href */
  }

  return (
    <a
      ref={heroRef}
      href={href}
      target="_blank"
      rel="sponsored noopener noreferrer"
      onClick={handleClick}
      aria-label={`${partner.name} — official channel`}
      className="group block overflow-hidden rounded-2xl border border-[#e3dfd3] transition-all duration-200 hover:border-[#0c5b41]/50 hover:shadow-[0_0_0_1px_rgba(12,91,65,0.25)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0c5b41]/60"
    >
      <img
        src={partner.heroSrc}
        width={1200}
        height={628}
        alt={partner.alt}
        loading="eager"
        decoding="async"
        className="block h-auto w-full"
      />
    </a>
  );
}
